// Single source of truth for bill-type identifiers used across the landlord
// upload flow, the tenant bill view, and the pending-bill approval screen.
// Historically each of those three screens invented its own spelling for the
// same 5 concepts (e.g. "electric" vs "electricity" vs "kelectric") — every
// consumer below should import from here instead of hardcoding strings.

export const BILL_TYPES = {
  RENT: "rent",
  ELECTRICITY: "electricity",
  GAS: "gas",
  WATER: "water",
  MAINTENANCE: "maintenance",
  SECURITY: "security",
  TRASH: "trash",
  // Water + security + trash, billed together as one bill (see the server's
  // billingCycle.mergeFixedCharges). Maintenance, rent, electricity and gas stay
  // separate.
  FIXED: "fixed",
};

export const BILL_TYPE_META = {
  [BILL_TYPES.RENT]: {
    title: "Rent",
    company: "Monthly Rent",
    icon: "🏠",
    type: BILL_TYPES.RENT,
    color: "#7C3AED",
    bgColor: "#EDE9FE",
    lightColor: "#DDD6FE",
  },
  [BILL_TYPES.ELECTRICITY]: {
    title: "Electricity",
    company: "K-Electric",
    icon: "⚡",
    type: BILL_TYPES.ELECTRICITY,
    color: "#FFB020",
    bgColor: "#FFF8E6",
    lightColor: "#FFF4D6",
  },
  [BILL_TYPES.GAS]: {
    title: "Gas",
    company: "SSGC",
    icon: "🔥",
    type: BILL_TYPES.GAS,
    color: "#FF6B35",
    bgColor: "#FFF0EC",
    lightColor: "#FFE5DD",
  },
  [BILL_TYPES.WATER]: {
    title: "Water",
    company: "Water Utility",
    icon: "💧",
    type: BILL_TYPES.WATER,
    color: "#0EA5E9",
    bgColor: "#E0F2FE",
    lightColor: "#D0EBFD",
  },
  [BILL_TYPES.MAINTENANCE]: {
    title: "Maintenance",
    company: "Property Fees",
    icon: "🔧",
    type: BILL_TYPES.MAINTENANCE,
    color: "#10B981",
    bgColor: "#D1FAE5",
    lightColor: "#C0F5D9",
  },
  [BILL_TYPES.SECURITY]: {
    title: "Security",
    company: "Security Charges",
    icon: "🛡️",
    type: BILL_TYPES.SECURITY,
    color: "#6366F1",
    bgColor: "#E0E7FF",
    lightColor: "#D4DBFF",
  },
  [BILL_TYPES.FIXED]: {
    title: "Fixed Charges",
    company: "Water · Security · Trash",
    icon: "🏢",
    type: BILL_TYPES.FIXED,
    color: "#0D9488",
    bgColor: "#CCFBF1",
    lightColor: "#99F6E4",
  },
  [BILL_TYPES.TRASH]: {
    title: "Trash",
    company: "Waste Management",
    icon: "🗑️",
    type: BILL_TYPES.TRASH,
    color: "#F97316",
    bgColor: "#FFEDD5",
    lightColor: "#FED7AA",
  },
};

// Types with a real utility-provider auto-fetch integration (K-Electric / SSGC).
export const AUTO_FILL_TYPES = [BILL_TYPES.ELECTRICITY, BILL_TYPES.GAS];

// Types where a bill image/PDF is mandatory on upload.
export const BILL_IMAGE_REQUIRED_TYPES = [BILL_TYPES.ELECTRICITY, BILL_TYPES.GAS];

// Types that can be configured at property registration as monthly OR annual
// (rent/electricity/gas are always-monthly and not configurable this way).
export const FREQUENCY_CAPABLE_TYPES = [
  BILL_TYPES.MAINTENANCE,
  BILL_TYPES.WATER,
  BILL_TYPES.SECURITY,
  BILL_TYPES.TRASH,
];

// Legacy/local spelling -> canonical key. Used to defensively normalize any
// type string coming from older stored state, URL params, or copy-pasted code.
const LEGACY_TO_CANONICAL = {
  electric: BILL_TYPES.ELECTRICITY,
  kelectric: BILL_TYPES.ELECTRICITY,
  electricity: BILL_TYPES.ELECTRICITY,
  gas: BILL_TYPES.GAS,
  ssgc: BILL_TYPES.GAS,
  water: BILL_TYPES.WATER,
  maintenance: BILL_TYPES.MAINTENANCE,
  maintainance: BILL_TYPES.MAINTENANCE,
  trash: BILL_TYPES.TRASH,
  security: BILL_TYPES.SECURITY,
  rent: BILL_TYPES.RENT,
  fixed: BILL_TYPES.FIXED,
  fixedcharges: BILL_TYPES.FIXED,
};

export const normalizeBillType = (raw) =>
  LEGACY_TO_CANONICAL[String(raw || "").toLowerCase()] ?? raw;

// Canonical key -> the exact field name the *existing* backend responses use
// (GetBill / GetTenantCurrentBill) and the word baked into the approve-*-bill
// endpoints. These spellings are already live in production and can't be
// changed from the frontend, so this map is the one place that knowledge
// lives now instead of being copy-pasted across 3 files.
// `rent`/`security` are new — best-effort canonical spelling, unverified
// against a real backend response (see plan notes).
export const BACKEND_KEY = {
  [BILL_TYPES.ELECTRICITY]: "electricity",
  [BILL_TYPES.GAS]: "ssgc",
  [BILL_TYPES.WATER]: "water",
  [BILL_TYPES.MAINTENANCE]: "maintainance", // matches existing (misspelled) backend field
  [BILL_TYPES.TRASH]: "trash",
  [BILL_TYPES.SECURITY]: "security",
  [BILL_TYPES.RENT]: "rent",
  [BILL_TYPES.FIXED]: "fixedCharges",
};

// Canonical key -> the exact string the upload form has always POSTed as the
// `type` FormData field for the 5 pre-existing categories (kept byte-for-byte
// identical to avoid any risk of breaking backend handling of that field).
// Rent/Security have no prior precedent, so they use their canonical spelling.
export const UPLOAD_TYPE_VALUE = {
  [BILL_TYPES.ELECTRICITY]: "electric",
  [BILL_TYPES.GAS]: "gas",
  [BILL_TYPES.WATER]: "water",
  [BILL_TYPES.MAINTENANCE]: "maintenance",
  [BILL_TYPES.TRASH]: "trash",
  [BILL_TYPES.SECURITY]: "security",
  [BILL_TYPES.RENT]: "rent",
  [BILL_TYPES.FIXED]: "fixed",
};
